import type {
    BeginSyncOptions,
    OfflineError,
    OfflineScene,
    OfflineSceneState,
    SyncOperation,
    SyncResult,
} from "@novorender/api";

import { assertNever } from "utils/assertNever";

import {
    createFullSyncHostReporter,
    type DownloadErrorCopyKey,
    getOfflineDownloadErrorCode,
    getOfflineDownloadErrorMessage,
    type OfflineDownloadStatusUpdate,
} from "./hostEvents";
import { asOfflineError, recordFromState, sceneUpdateFromResult } from "./offlineRecord";
import type { OfflineSceneRecord } from "./offlineSlice";
import { trackOfflineFailure } from "./offlineTelemetry";

/** The widget's scene records, by directory name. */
export type OfflineRecords = {
    get(sceneDirName: string): OfflineSceneRecord | undefined;
    /** `undefined` removes the record. */
    set(sceneDirName: string, record: OfflineSceneRecord | undefined): void;
};

/** What one full sync of the open scene needs from the viewer. */
export type FullSyncRequest = {
    /** For a failure before the scene is had. */
    sceneDirName: string;
    openScene(): Promise<OfflineScene>;
    persistStorage(): Promise<void>;
    /** @throws When the scene has no index to sync from. */
    sceneIndexUrl(): URL;
    /** What the widget lists the scene as; `undefined` keeps the name its record holds. Never rejects. */
    name(): Promise<string | undefined>;
    /** The mini map bytes the sync covers (see `OfflineSceneRecord.minimapBytes`). Never rejects. */
    minimapBytes?(signal: AbortSignal): Promise<number | undefined>;
    /** A write under way that holds the scene's sync lock, waited for rather than fail busy. Never rejects. */
    lockHeld(): Promise<void> | undefined;
    options(scene: OfflineScene, sceneIndexUrl: URL): Omit<BeginSyncOptions, "name">;
    reportToHost(update: OfflineDownloadStatusUpdate): void;
    translate(key: DownloadErrorCopyKey): string;
    /** Told once the sync has begun. */
    onBegin(): void;
};

/**
 * How long a full sync waits for the scene's name before it begins without one: a stalled request
 * shouldn't hold the download for good.
 */
export const NAME_LOOKUP_TIMEOUT_MS = 30_000;

/** As {@link NAME_LOOKUP_TIMEOUT_MS}, for the estimate of the mini map's bytes. */
const MINIMAP_ESTIMATE_TIMEOUT_MS = 60_000;

/** One start of a full sync, until its sync settles or is paused. */
type Run = {
    /** Counting from 1: the latest run to start owns the record (see `recordOwner`). */
    readonly runNumber: number;
    sync?: SyncOperation;
};

/**
 * This tab's full syncs of the open scene: one at a time, each reporting its own outcome to the
 * host, while the record shows only the latest run's. A resume after a pause starts a new run
 * while the paused sync may still be settling.
 */
export class FullSyncController {
    private runsStarted = 0;
    /** The run whose outcome the record shows. */
    private recordOwner = 0;
    /** The run starting its sync or awaiting it: what a pause pauses. */
    private current: Run | undefined;
    /** Each sync begun and not settled, a paused one included, by its run's number. */
    private readonly unsettled = new Map<SyncOperation, number>();
    private pauseController = new AbortController();

    constructor(private readonly records: OfflineRecords) {}

    /** Whether a run is starting its sync or awaiting it: a start would begin nothing. */
    get isRunning(): boolean {
        return this.current !== undefined;
    }

    /** Whether a sync this tab began hasn't settled: until then, a "syncing" state is its own. */
    get hasUnsettledSync(): boolean {
        return this.unsettled.size > 0;
    }

    /** Runs one full sync until its outcome is reported; nothing while another run is under way. */
    async start(request: FullSyncRequest): Promise<void> {
        if (this.current) {
            return;
        }
        const run: Run = { runNumber: ++this.runsStarted };
        this.recordOwner = run.runNumber;
        this.current = run;
        try {
            await this.run(run, request);
        } finally {
            if (this.current === run) {
                this.current = undefined;
            }
        }
    }

    /** Pauses the run under way. A resume needn't wait for its sync to settle: it starts a new run. */
    pause(): void {
        this.pauseController.abort();
        this.pauseController = new AbortController();
        this.current?.sync?.pause();
        this.current = undefined;
    }

    /** A sync doesn't outlive the viewer that began it, which could no longer pause it. */
    dispose(): void {
        this.pauseController.abort();
        this.pauseController = new AbortController();
        this.current?.sync?.pause();
    }

    private async run(run: Run, request: FullSyncRequest): Promise<void> {
        // Captured up front: `pause()` replaces the controller as it aborts it.
        const pauseSignal = this.pauseController.signal;
        // Asked alongside the preparation: on Procore it is a request.
        const naming = settleWithin(request.name(), NAME_LOOKUP_TIMEOUT_MS, pauseSignal);
        const minimapEstimate = new AbortController();
        const estimatingMinimap = request.minimapBytes
            ? settleWithin(request.minimapBytes(minimapEstimate.signal), MINIMAP_ESTIMATE_TIMEOUT_MS, pauseSignal)
            : undefined;
        const isCurrent = () => this.recordOwner === run.runNumber;

        let scene: OfflineScene;
        let sceneIndexUrl: URL;
        try {
            scene = await request.openScene();
        } catch (e) {
            await this.failToStart(request, isCurrent, e);
            return;
        }
        const beforeRun = this.records.get(scene.id);
        try {
            await request.persistStorage();
            sceneIndexUrl = request.sceneIndexUrl();
        } catch (e) {
            await this.failToStart(request, isCurrent, e, { scene, beforeRun });
            return;
        }
        const name = await naming;
        const minimapBytes = await estimatingMinimap;
        // A timeout or a pause settles the estimate without stopping its requests.
        minimapEstimate.abort();
        await request.lockHeld();

        if (pauseSignal.aborted) {
            await this.showPausedBeforeBegin(request, scene, isCurrent, beforeRun);
            return;
        }

        const sync = scene.beginSync(sceneIndexUrl, {
            ...(name !== undefined && { name }),
            ...request.options(scene, sceneIndexUrl),
        });
        run.sync = sync;
        this.unsettled.set(sync, run.runNumber);

        // As the sync records it: the name it was given, or the one the record holds.
        const shownName = name ?? beforeRun?.name;
        this.records.set(scene.id, {
            id: scene.id,
            view: { kind: "syncing", progress: undefined },
            ...(shownName !== undefined ? { name: shownName } : {}),
            ...(beforeRun?.legacyLastSynced ? { legacyLastSynced: beforeRun.legacyLastSynced } : {}),
            ...(minimapBytes !== undefined ? { minimapBytes } : {}),
        });

        const hostReporter = createFullSyncHostReporter(request.reportToHost, request.translate);
        const removeProgressListener = sync.addEventListener("progress", (progress) => {
            hostReporter.progress(progress);
            if (isCurrent()) {
                this.update(scene.id, { view: { kind: "syncing", progress } });
            }
        });
        request.onBegin();

        const result = await sync.done;
        removeProgressListener();
        this.unsettled.delete(sync);

        hostReporter.done(result);
        trackSyncResult(result, scene);
        if (!isCurrent()) {
            return;
        }

        if (result.kind === "failed" && result.error.kind === "busy") {
            // Another tab holds the scene, syncing it most likely: show its sync, which only it can
            // pause. The host still hears "error", as the published mapping says.
            const state = await scene.getState().catch(() => undefined);
            if (!isCurrent()) {
                return;
            }
            if (state?.kind === "syncing") {
                this.showState(scene.id, state, false);
                return;
            }
        }

        if (result.kind !== "completed") {
            await this.showStoppedSync(scene, result, isCurrent, beforeRun);
            return;
        }

        this.update(scene.id, {
            ...sceneUpdateFromResult(result, this.records.get(scene.id), new Date()),
            syncingElsewhere: false,
        });
        // The version synced and the mini map's standing, which the result doesn't carry.
        const state = await scene.getState().catch(() => undefined);
        if (state?.kind === "synced" && isCurrent() && !this.hasUnsettledSync) {
            this.update(scene.id, { view: state });
        }
    }

    /**
     * A failure before the SDK's sync begins, reported as a failed sync is. A run paused and
     * resumed meanwhile began nothing and told the host nothing: the resumed run speaks for it.
     */
    private async failToStart(
        request: FullSyncRequest,
        isCurrent: () => boolean,
        error: unknown,
        opened?: { scene: OfflineScene; beforeRun: OfflineSceneRecord | undefined },
    ) {
        void trackOfflineFailure({ error, source: "offline.useHandleOffline.fullSync", scene: opened?.scene });
        if (!isCurrent()) {
            return;
        }
        const offlineError = asOfflineError(error);
        request.reportToHost({
            status: "error",
            error: getOfflineDownloadErrorMessage(offlineError.kind, request.translate),
            errorCode: getOfflineDownloadErrorCode(offlineError.kind),
        });
        const before = this.records.get(request.sceneDirName);
        switch (before?.view.kind) {
            case "failed":
                // An earlier run's failure after its claim: the directory still reads as it left it.
                this.records.set(request.sceneDirName, {
                    ...before,
                    view: { ...before.view, error: offlineError },
                    syncingElsewhere: false,
                });
                break;
            case "syncing":
                // A run this one superseded still shows, whose outcome it never took.
                this.records.set(request.sceneDirName, {
                    ...before,
                    view: { kind: "failed", error: offlineError, downloaded: undefined },
                    syncingElsewhere: false,
                });
                break;
            default:
                this.showRunFailure(request.sceneDirName, before, offlineError);
        }
        if (opened) {
            await this.discardUnclaimedDirectory(opened.scene, opened.beforeRun, isCurrent);
        }
    }

    private async showPausedBeforeBegin(
        request: FullSyncRequest,
        scene: OfflineScene,
        isCurrent: () => boolean,
        beforeRun: OfflineSceneRecord | undefined,
    ) {
        if (!isCurrent()) {
            return;
        }
        // Run numbers only grow, so the highest is the latest run still settling.
        const settling = Math.max(0, ...this.unsettled.values());
        if (settling > 0) {
            // A sync paused for this run's resume is still settling: it reports how it ends. Read
            // now, the state would show it running, with no one left to update the record.
            this.recordOwner = settling;
            return;
        }
        request.reportToHost({ status: "aborted" });
        let state: OfflineSceneState;
        try {
            state = await scene.getState();
        } catch (e) {
            // A sync this run superseded may still show, whose outcome it never took: say what is
            // known rather than leave up a sync that isn't running.
            const shown = this.records.get(scene.id);
            if (isCurrent() && shown?.view.kind === "syncing" && !shown.syncingElsewhere) {
                this.update(scene.id, {
                    view: { kind: "failed", error: asOfflineError(e), downloaded: undefined },
                    syncingElsewhere: false,
                });
            }
            return;
        }
        if (isCurrent()) {
            this.showState(scene.id, state, this.hasUnsettledSync);
            await this.discardUnclaimedDirectory(scene, beforeRun, isCurrent);
        }
    }

    /**
     * Shows a sync that stopped short of completing. The record shows what the directory reads,
     * except while another sync of this tab is still settling, when it shows the result. A sync
     * stopped in its scan has claimed nothing, so its result alone would offer an intact copy for
     * Discard.
     * @param beforeRun The record before the sync began.
     */
    private async showStoppedSync(
        scene: OfflineScene,
        result: Extract<SyncResult, { kind: "paused" | "failed" }>,
        isCurrent: () => boolean,
        beforeRun: OfflineSceneRecord | undefined,
    ) {
        const state = await scene.getState().catch(() => undefined);
        if (!isCurrent()) {
            return;
        }
        const shown = this.records.get(scene.id);
        const showResult = () =>
            this.update(scene.id, { ...sceneUpdateFromResult(result, shown, new Date()), syncingElsewhere: false });
        if (this.hasUnsettledSync) {
            showResult();
            return;
        }
        const claimed = state?.kind === "paused" || state?.kind === "interrupted";

        // Unreadable now, the scene is taken to be as it was before this run.
        if (state?.kind === "synced" || (!state && beforeRun?.view.kind === "synced")) {
            // Still whole. A failure is news on the synced card, and a pause is none.
            const whole = state ? recordFromState(scene.id, state, false, shown) : beforeRun;
            if (result.kind === "failed") {
                this.showRunFailure(scene.id, whole, result.error);
            } else if (whole) {
                this.records.set(scene.id, { ...whole, syncingElsewhere: false });
            }
        } else if (state && !claimed && result.kind === "paused") {
            // Paused before its claim: as the directory reads (nothing offline, incremental).
            this.showState(scene.id, state, false);
            await this.discardUnclaimedDirectory(scene, beforeRun, isCurrent);
        } else if (state?.kind === "paused" && result.kind === "paused") {
            // With the sync's target, which the result doesn't say.
            this.showState(scene.id, state, false);
        } else if (state && !claimed && result.kind === "failed") {
            // The sync failed before it claimed the scene, so the scene is as the directory reads.
            this.showRunFailure(scene.id, recordFromState(scene.id, state, false, shown), result.error);
            // Failed busy, the directory is the other tab's.
            if (state.kind === "absent" && result.error.kind !== "busy") {
                await this.discardUnclaimedDirectory(scene, beforeRun, isCurrent);
            }
        } else {
            // The sync claimed the scene, which now reads paused or interrupted. Or the state is
            // unreadable, and nothing synced stood before the run.
            showResult();
        }
    }

    /**
     * Removes the directory `scene()` made for a run that stopped before its sync claimed it. The
     * SDK recorded nothing, but left there the engine would cache into it, and the scene would list
     * as incremental though the user asked for a full download. A scene that held something before
     * the run (an enrolment, say) wants the directory. An earlier run's failure with nothing
     * offline doesn't count. A resume started meanwhile keeps it: `delete()` would pause the
     * resumed sync.
     */
    private async discardUnclaimedDirectory(
        scene: OfflineScene,
        beforeRun: OfflineSceneRecord | undefined,
        isCurrent: () => boolean,
    ) {
        if (beforeRun && beforeRun.view.kind !== "absent") {
            return;
        }
        try {
            const state = await scene.getState();
            if (state.kind === "absent" && isCurrent() && !this.hasUnsettledSync) {
                await scene.delete();
            }
        } catch {
            // Best effort: left behind, it lists as incremental once the engine caches into it.
        }
    }

    /**
     * Shows a run that failed before it claimed the scene: the scene as it was, `base`, with the
     * failure. A scene with nothing offline gets an `absent` view, so that the card can show why.
     */
    private showRunFailure(sceneDirName: string, base: OfflineSceneRecord | undefined, error: OfflineError) {
        this.records.set(sceneDirName, {
            ...(base ?? { view: { kind: "absent" } }),
            id: sceneDirName,
            runFailure: error,
            syncingElsewhere: false,
        });
    }

    private showState(sceneDirName: string, state: OfflineSceneState, ownSync: boolean) {
        this.records.set(sceneDirName, recordFromState(sceneDirName, state, ownSync, this.records.get(sceneDirName)));
    }

    private update(sceneDirName: string, updates: Partial<OfflineSceneRecord>) {
        const previous = this.records.get(sceneDirName);
        if (previous) {
            this.records.set(sceneDirName, { ...previous, ...updates });
        }
    }
}

/** What a sync's outcome reports to telemetry. The user-facing reports are the host's and the widget's. */
function trackSyncResult(result: SyncResult, scene: OfflineScene): void {
    switch (result.kind) {
        case "completed":
            switch (result.minimap.kind) {
                case "notRequested":
                case "synced":
                    return;
                case "failed":
                    // The sync completed: the mini map isn't needed to open the scene.
                    void trackOfflineFailure({
                        error: result.minimap.error,
                        source: "offline.useHandleOffline.syncMinimap",
                        scene,
                    });
                    return;
                default:
                    return assertNever(result.minimap);
            }
        case "paused":
            return;
        case "failed":
            void trackOfflineFailure({ error: result.error, source: "offline.useHandleOffline.fullSync", scene });
            return;
        default:
            return assertNever(result);
    }
}

/** What `promise` resolves to, or `undefined` once `ms` have passed or `signal` aborts, whichever is first. */
function settleWithin<T>(promise: Promise<T>, ms: number, signal: AbortSignal): Promise<T | undefined> {
    return new Promise((resolve) => {
        const settle = (value: T | undefined) => {
            clearTimeout(timer);
            signal.removeEventListener("abort", abort);
            resolve(value);
        };
        const abort = () => settle(undefined);
        const timer = setTimeout(abort, ms);
        if (signal.aborted) {
            abort();
            return;
        }
        signal.addEventListener("abort", abort, { once: true });
        promise.then(settle, abort);
    });
}
